import { Plus, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../../../components/ui/badge";
import { Input } from "../../../../../components/ui/input";
import { SkillKind, SkillStatus } from "../../../../../services/skills/skillsEndpoints.interface";
import useSkillsStore, { StructuredOutputField } from "../../../../../store/skills/skillsStore";

const statusTone = (status?: string): "active" | "pending" | "open" => {
  switch (status) {
    case "active":
      return "active";
    case "draft":
      return "pending";
    default:
      return "open";
  }
};

const FIELD_TYPES: StructuredOutputField["fieldType"][] = ["string", "number", "boolean", "array", "object"];

const SkillMetadataForm = () => {
  const editor = useSkillsStore((s) => s.editor);
  const setEditor = useSkillsStore((s) => s.setEditor);
  const addStructuredOutputField = useSkillsStore((s) => s.addStructuredOutputField);
  const updateStructuredOutputField = useSkillsStore((s) => s.updateStructuredOutputField);
  const removeStructuredOutputField = useSkillsStore((s) => s.removeStructuredOutputField);
  const [keywordInput, setKeywordInput] = useState("");

  const handleAddKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && keywordInput.trim()) {
      e.preventDefault();
      const keyword = keywordInput.trim().toLowerCase();
      if (!editor.keywords.includes(keyword)) {
        setEditor({ keywords: [...editor.keywords, keyword] });
      }
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setEditor({ keywords: editor.keywords.filter((k) => k !== keyword) });
  };

  const generateSlug = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-foreground">Details</h2>
        <Badge variant="statusPill" status={statusTone(editor.status)}>
          {editor.status.charAt(0).toUpperCase() + editor.status.slice(1)}
        </Badge>
      </div>

      {/* Display Name */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Display Name
        </label>
        <Input
          placeholder="e.g. Churn Predictor"
          value={editor.displayName}
          onChange={(e) => {
            const displayName = e.target.value;
            setEditor({
              displayName,
              name: generateSlug(displayName),
            });
          }}
        />
      </div>

      {/* Slug (read-only, auto-generated) */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Slug
        </label>
        <Input
          value={editor.name}
          readOnly
          className="bg-muted/50 text-muted-foreground cursor-default"
        />
        <span className="text-[11px] text-muted-foreground">
          Auto-generated from display name
        </span>
      </div>

      {/* Description */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Description
        </label>
        <textarea
          placeholder="What does this skill do?"
          value={editor.description}
          onChange={(e) => setEditor({ description: e.target.value })}
          rows={3}
          className="flex w-full rounded-[5px] border border-border-chip bg-card px-2 py-1.5 text-[13px] ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary resize-none"
        />
      </div>

      {/* Kind */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Kind
        </label>
        <select
          value={editor.kind}
          onChange={(e) => setEditor({ kind: e.target.value as SkillKind })}
          className="flex h-8 w-full rounded-[5px] border border-border-chip bg-card px-2 py-1 text-[13px] ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary"
        >
          <option value={SkillKind.CAPABILITY}>Capability</option>
          <option value={SkillKind.PLAYBOOK}>Playbook</option>
        </select>
        <span className="text-[11px] text-muted-foreground">
          {editor.kind === SkillKind.CAPABILITY
            ? "A reusable ability the agent can invoke."
            : "A step-by-step workflow the agent follows."}
        </span>
      </div>

      {/* Priority */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Priority
        </label>
        <Input
          type="number"
          min={1}
          max={10}
          value={editor.priority}
          onChange={(e) =>
            setEditor({
              priority: Math.max(1, Math.min(10, Number(e.target.value) || 1)),
            })
          }
        />
        <span className="text-[11px] text-muted-foreground">
          1 (highest) to 10 (lowest)
        </span>
      </div>

      {/* Visibility */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Visibility
        </label>
        <select
          value={editor.visibility || "workspace"}
          onChange={(e) => setEditor({ visibility: e.target.value as "workspace" | "private" })}
          className="flex h-8 w-full rounded-[5px] border border-border-chip bg-card px-2 py-1 text-[13px] ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary"
        >
          <option value="workspace">Workspace</option>
          <option value="private">Private</option>
        </select>
        <span className="text-[11px] text-muted-foreground">
          {editor.visibility === "private"
            ? "Only you can use this skill in chat. Not available in signals."
            : "Available for signals, automations, and all workspace members."}
        </span>
      </div>

      {/* Keywords */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-muted-foreground">
          Trigger Keywords
        </label>
        <Input
          placeholder="Type and press Enter"
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={handleAddKeyword}
        />
        {editor.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {editor.keywords.map((keyword) => (
              <Badge
                key={keyword}
                variant="secondary"
                className="gap-1 pr-1 cursor-pointer"
                onClick={() => handleRemoveKeyword(keyword)}
              >
                {keyword}
                <X className="h-3 w-3" />
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Structured Output */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-muted-foreground">
            Structured Output
          </label>
          <button
            type="button"
            onClick={addStructuredOutputField}
            className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
          >
            <Plus className="h-3 w-3" />
            Add field
          </button>
        </div>
        {editor.structuredOutput.length === 0 && (
          <span className="text-[11px] text-muted-foreground">
            No fixed output format. Add fields if you want structured responses.
          </span>
        )}
        {editor.structuredOutput.map((field, i) => (
          <div key={i} className="flex flex-col gap-1.5 rounded-md border border-border-chip p-2">
            <div className="flex items-center gap-1.5">
              <Input
                placeholder="Field name"
                value={field.fieldName}
                onChange={(e) => updateStructuredOutputField(i, { fieldName: e.target.value })}
                className="flex-1 h-7 text-xs"
              />
              <select
                value={field.fieldType}
                onChange={(e) => updateStructuredOutputField(i, { fieldType: e.target.value as StructuredOutputField["fieldType"] })}
                className="h-7 rounded-[5px] border border-border-chip bg-card px-1.5 text-[11px] ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeStructuredOutputField(i)}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
            <Input
              placeholder="Description (optional)"
              value={field.description}
              onChange={(e) => updateStructuredOutputField(i, { description: e.target.value })}
              className="h-7 text-xs"
            />
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => updateStructuredOutputField(i, { required: e.target.checked })}
                className="rounded"
              />
              Required
            </label>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillMetadataForm;
